import { fmtTimeHM } from '../../extensions/utils';

export const green = '#e0efd5';
export const greenDark = '#2f6f00';
export const orangeLight = '#ffcd9c';
export const orangeDark = '#f18408';

// Маппер для данных для графиков: преобразует полученные с сервера series в составе JSON
// в готовые данные для echarts, включая оформление
const DashboardMapperChart = (key, apiData) => {
  switch (key) {
    case 'visits': {
      const series = apiData.visits?.series || [];
      return {
        xAxis: {
          type: 'category',
          data: series.map(item => fmtTimeHM(item.hour)),
          axisLine: { lineStyle: { color: greenDark } },
          axisLabel: { color: greenDark }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: greenDark } },
          axisLabel: { color: greenDark },
          splitLine: { lineStyle: { color: greenDark } }
        },
        series: [{
          data: series.map(item => item.qty),
          type: 'line',
          smooth: true,
          lineStyle: { color: orangeDark, width: 3 },
          itemStyle: { color: greenDark },
          areaStyle: { color: orangeLight }
        }],
        grid: { left: 40, right: 10, top: 10, bottom: 20 },
        tooltip: {},
      };
    }
    case 'records': {
      const series = apiData.records?.series || [];
      return {
        xAxis: {
          type: 'category',
          data: series.map(item => {
            if (typeof item.hour === 'string' && item.hour.includes(':')) {
              return item.hour.substring(0, 5); // Отсекаем секунды из HH:mm:ss
            }
            return item.hour || '';
          }),
          axisLine: { lineStyle: { color: greenDark } },
          axisLabel: { color: greenDark }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: greenDark } },
          axisLabel: { color: greenDark, interval: 1 },
          splitLine: { lineStyle: { color: greenDark } },
          minInterval: 1
        },
        series: [{
          data: series.map(item => item.qty),
          type: 'bar',
          itemStyle: { color: orangeDark }
        }],
        grid: { left: 40, right: 10, top: 10, bottom: 20 },
        tooltip: {},
      };
    }
    case 'clients': {
      const series = apiData.clients?.series || [];
      return {
        xAxis: {
          type: 'category',
          data: series.map(item => {
            const date = new Date(item.date);
            return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
          }),
          axisLine: { lineStyle: { color: greenDark } },
          axisLabel: { color: greenDark }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: green } },
          axisLabel: { color: greenDark },
          splitLine: { lineStyle: { color: greenDark } }
        },
        series: [{
          data: series.map(item => item.qty ?? 0),
          type: 'bar',
          itemStyle: { color: orangeDark }
        }],
        grid: { left: 40, right: 10, top: 10, bottom: 20 },
        tooltip: {},
      };
    }
    case 'finance': {
      const series = apiData.finance?.series || [];
      
      // Группируем данные по датам (используем локальное время пользователя)
      const groupedByDate = {};
      series.forEach(item => {
        // Используем локальную дату пользователя из строки даты
        const dateParts = item.docdate.split(/[-T:]/).map(Number);
        const date = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
        const dateKey = date.toLocaleDateString('sv-SE'); // "YYYY-MM-DD"
        
        if (!groupedByDate[dateKey]) {
          groupedByDate[dateKey] = {
            date: dateKey,
            goods: 0,
            services: 0,
            membership: 0,
            packages: 0
          };
        }
        
        // Суммируем по категориям
        switch (item.vid) {
          case 0: groupedByDate[dateKey].goods += Math.max(0, item.sum); break;
          case 1: groupedByDate[dateKey].services += Math.max(0, item.sum); break;
          case 2: groupedByDate[dateKey].membership += Math.max(0, item.sum); break;
          case 3: groupedByDate[dateKey].packages += Math.max(0, item.sum); break;
        }
      });

      // Создаем массив дат за последние 30 дней, включая текущую (локальную дату пользователя)
      const userToday = new Date(); // Локальная дата пользователя
      userToday.setHours(0, 0, 0, 0);
      
      const dateRange = Array.from({ length: 30 }, (_, i) => {
        const date = new Date(userToday);
        date.setDate(date.getDate() - (29 - i)); // От 29 дней назад до сегодня (30 дней)
        return date.toLocaleDateString('sv-SE');
      });

      // Создаем заполненный массив данных для всех дат
      const fullData = dateRange.map(dateKey => {
        return groupedByDate[dateKey] || {
          date: dateKey,
          goods: 0,
          services: 0,
          membership: 0,
          packages: 0
        };
      });

      // Форматируем даты для отображения (локальный формат пользователя)
      const formatDateLocal = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(navigator.language, { 
          day: '2-digit', 
          month: '2-digit'
        });
      };

      return {
        xAxis: {
          type: 'category',
          data: fullData.map(item => formatDateLocal(item.date)),
          axisLine: { lineStyle: { color: greenDark } },
          axisLabel: { 
            color: greenDark,
            formatter: (value, index) => {
              return index % 2 === 0 || index === fullData.length - 1 ? value : '';
            },
          },
          axisTick: { alignWithLabel: true }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: green } },
          axisLabel: { color: greenDark },
          splitLine: { lineStyle: { color: greenDark } }
        },
        series: [
          {
            name: 'Товары+Услуги',
            data: fullData.map(item => item.goods + item.services),
            type: 'bar',
            stack: 'total',
            itemStyle: { color: orangeDark }
          },
          {
            name: 'Абонементы+Пакеты',
            data: fullData.map(item => item.membership + item.packages),
            type: 'bar',
            stack: 'total',
            itemStyle: { color: greenDark }
          }
        ],
        grid: { left: 75, right: 10, top: 10, bottom: 40 },
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            const dataIndex = params[0].dataIndex;
            const date = new Date(fullData[dataIndex].date);
            const dateStr = date.toLocaleDateString(navigator.language, { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            });
            
            let result = `${dateStr}<br>`;
            params.forEach(param => {
              result += `${param.marker} ${param.seriesName}: ${param.value.toLocaleString(navigator.language)} руб.<br>`;
            });
            return result;
          }
        },
        legend: {
          data: ['Товары+Услуги', 'Абонементы+Пакеты'],
          textStyle: { fontFamily: "Comfortaa", fontSize: 10, color: greenDark },
          orient: 'horizontal',
          bottom: 0,
          left: 'center',
        }
      };
    }
    default:
      return {};
  }
}

export default DashboardMapperChart;